import React from 'react';

import { Add as AddIcon, BorderColor as BorderColorIcon } from '@mui/icons-material';
import { Button } from '@mui/material';

import { STATUSES_FILTER_OPTIONS } from '@clearverify/constants';
import { formatPhoneNumber, parseNullableValue } from '@clearverify/utils';

import { PeoPayrollProviderStaffInfoFragment } from 'src/api';
import { ClickableLabel, UserStatusChip } from 'src/components/common';
import {
  Table,
  TableColumn,
  TableFilter,
  TableRowAction,
  TableThemes,
  TableTopAction,
} from 'src/components/table';
import { ModalId } from 'src/constants/modals';
import { useSearch } from 'src/hooks/common';
import { useModal } from 'src/hooks/shared';
import { PaginationContextValue } from 'src/providers/PaginationProvider';

export interface PeoPayrollProviderStaffTableProps {
  peoPayrollProviderId: string | undefined;
  peoPayrollProviderStaffList?: PeoPayrollProviderStaffInfoFragment[];
  peoPayrollProviderStaffCount?: number;
  onRefresh?: () => Promise<void>;
  pagination: PaginationContextValue;
  loading: boolean;
}

export const PeoPayrollProviderStaffTable: React.FC<PeoPayrollProviderStaffTableProps> = ({
  peoPayrollProviderId,
  peoPayrollProviderStaffList = [],
  peoPayrollProviderStaffCount = 0,
  onRefresh,
  pagination,
  loading,
}) => {
  const { setSearchValue, searchValue } = useSearch();
  const { openModal } = useModal();

  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect must not depend on "pagination" here
  React.useEffect(() => {
    pagination.resetPage();
  }, [searchValue]);

  const rowActions = React.useMemo(
    (): Array<TableRowAction<PeoPayrollProviderStaffInfoFragment>> => [
      {
        startIcon: <BorderColorIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'View Details',
        onAction: (staff: PeoPayrollProviderStaffInfoFragment) => {
          openModal(ModalId.PeoPayrollProviderStaffDetailsModal, {
            peoPayrollProviderStaffId: staff.id,
            peoPayrollProviderId,
          });
        },
      },
    ],
    [openModal, peoPayrollProviderId],
  );

  const topActions = React.useMemo(
    (): Array<TableTopAction> => [
      {
        actionName: 'Add',
        onAction: () => openModal(ModalId.PeoPayrollProviderInviteStaffModal),
        icon: <AddIcon />,
        show: true,
        render() {
          return (
            <Button
              key={this.actionName}
              variant="contained"
              startIcon={this.icon}
              onClick={this.onAction}
            >
              New
            </Button>
          );
        },
      },
    ],
    [openModal],
  );

  return (
    <Table
      dataSource={peoPayrollProviderStaffList}
      rowCount={peoPayrollProviderStaffCount}
      columns={COLUMNS}
      pagination={pagination}
      onSearch={setSearchValue}
      loading={loading}
      rowActions={rowActions}
      topActions={topActions}
      filters={filters}
      tableTheme={TableThemes.withoutProfileIcon}
      onRefresh={onRefresh}
    />
  );
};

const COLUMNS: TableColumn<PeoPayrollProviderStaffInfoFragment>[] = [
  {
    title: 'Full Name',
    key: 'user.firstName',
    sortable: true,
    render: (_valueByDataPath, peoProviderStaff, { rowActions }) => {
      return (
        <ClickableLabel
          type="button"
          onClick={() => rowActions?.[0]?.onAction(peoProviderStaff)}
          label={peoProviderStaff.user.fullName}
          logoUrl={peoProviderStaff.user.avatar?.downloadUrl}
        />
      );
    },
  },
  {
    title: 'Phone Number',
    key: 'user.phoneNumber',
    render: (_valueByDataPath, peoProviderStaff) =>
      parseNullableValue(formatPhoneNumber(peoProviderStaff.user.phoneNumber)),
  },
  {
    title: 'Email',
    key: 'user.email',
    sortable: true,
    nonNullSortable: true,
    render: (_valueByDataPath, peoProviderStaff) => parseNullableValue(peoProviderStaff.user.email),
  },
  {
    title: 'Status',
    key: 'user.status',
    sortable: true,
    nonNullSortable: true,
    render: (_valueByDataPath, peoProviderStaff) => (
      <UserStatusChip userStatus={peoProviderStaff.user.status} />
    ),
  },
];

const filters: TableFilter[] = [
  {
    id: 'status',
    type: 'select',
    fieldProps: {
      label: 'Status',
      multiple: true,
      options: STATUSES_FILTER_OPTIONS,
    },
  },
];
